/**
 * Единственное место, где написано «телефон сейчас занят звонком».
 *
 * <p><b>Почему модуль, а не состояние React.</b> Решение «звонить или ответить
 * занято» принимается там, куда состояние экрана звонка не дотягивается: в
 * WS-приёмнике {@code GlobalRealtimeProvider}, в мосте входящего экрана и в
 * обработчике ответа CallKit. Разговор при этом идёт в другом чате, а его экран
 * может быть и вовсе размонтирован (пользователь ушёл в список чатов). Ссылку
 * на его состояние тянуть некуда — отсюда крошечный реестр на модульной
 * переменной.
 *
 * <p>Инвариант: непустое значение ровно тогда, когда у пользователя есть живой
 * звонок в любой фазе, кроме `idle`. Поэтому {@link setActiveCall} обязан
 * вызываться и при размонтировании экрана звонка — зависшая запись отвечала бы
 * «занято» на все входящие до перезапуска приложения.
 */

import type { ActiveCallSnapshot } from './callBusyRules';

/**
 * Кто именно записал звонок.
 *
 * <p><b>Зачем владелец.</b> {@code useVoiceCallSession} живёт в двух местах
 * сразу: на экране звонка и на экране чата под ним (там он нужен ради подписки
 * на сигналы и события звонка в переписке). Экран чата всегда в фазе `idle`,
 * и без проверки владельца его собственный «звонка нет» стирал бы запись,
 * сделанную живым разговором, — занятость сбрасывалась бы на первом же
 * перерисовывании чата.
 */
type Owner = object;

let owner: Owner | null = null;
let active: ActiveCallSnapshot | null = null;

/**
 * Кому сообщить, что телефон занялся звонком или освободился. Сейчас слушатель один — музыкальный
 * плеер: он ставит себя на паузу на время разговора. Системное прерывание аудиосессии для
 * этого не годится — звонок внутри приложения его не присылает, он просто перенастраивает
 * сессию под себя, и музыка играла бы поверх собеседника.
 */
type BusyListener = (busy: boolean) => void;
const busyListeners = new Set<BusyListener>();

export function subscribeCallBusy(listener: BusyListener): () => void {
  busyListeners.add(listener);
  return () => {
    busyListeners.delete(listener);
  };
}

function notifyBusy(wasBusy: boolean): void {
  const busy = active !== null;
  if (busy !== wasBusy) {
    busyListeners.forEach((l) => l(busy));
  }
}

/**
 * Отметить звонок владельца. `null` — «у меня звонка нет».
 *
 * <p>Запись звонка всегда забирает владение: живой разговор старше любой
 * прошлой записи. А снять запись может только её собственный владелец — см.
 * {@link Owner}.
 *
 * <p>Фаза `idle` приравнена к отсутствию звонка: экран звонка держит её и до
 * набора, и после отбоя, а «занято» в этих состояниях означало бы, что
 * открытый пустой экран блокирует входящие.
 */
export function setActiveCall(by: Owner, snapshot: ActiveCallSnapshot | null): void {
  const wasBusy = active !== null;
  if (snapshot && snapshot.phase !== 'idle') {
    owner = by;
    active = snapshot;
  } else if (owner === by) {
    owner = null;
    active = null;
  }
  notifyBusy(wasBusy);
}

export function getActiveCall(): ActiveCallSnapshot | null {
  return active;
}

/**
 * Идёт ли <b>чужой</b> звонок — то есть записанный не этим владельцем.
 *
 * <p>Нужно тем, кто на своей фазе `idle` возвращает аудиосессию приложению:
 * `useVoiceCallSession` живёт и на экране чата, где фаза всегда `idle`, и его
 * «звонок кончился, верните сессию» глушило живой разговор в другом чате —
 * достаточно было открыть переписку, пока идёт звонок.
 *
 * <p>Свой же собственный звонок сюда не попадает намеренно: когда разговор
 * заканчивается, сессию как раз и надо вернуть.
 */
export function isForeignActiveCall(by: Owner): boolean {
  return active !== null && owner !== by;
}

export function resetActiveCallForTests(): void {
  owner = null;
  active = null;
}
